import { useSearchParams } from 'react-router-dom';

/** Extracted rather than inlined so it is unit-testable — no RTL here. */
export const parseShowTab = <T extends string>(
  raw: string | null | undefined,
  tabs: readonly T[],
  defaultTab: T
): T =>
  (tabs as readonly string[]).includes(raw ?? '') ? (raw as T) : defaultTab;

/**
 * `?tab=` URL state for show views. The default tab deletes the param
 * so canonical URLs stay clean; `replace` keeps tab flips out of
 * history.
 */
export const useShowTab = <T extends string>(
  tabs: readonly T[],
  defaultTab: T
): [T, (key: string) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseShowTab(searchParams.get('tab'), tabs, defaultTab);
  const onTabChange = (key: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === defaultTab) next.delete('tab');
        else next.set('tab', key);
        return next;
      },
      { replace: true }
    );
  return [activeTab, onTabChange];
};
